import React from 'react';
import { motion } from 'framer-motion';

interface LoadingButtonProps {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  loadingText: string;
  children: React.ReactNode;
  ariaLabel?: string;
}

/**
 * LoadingButton Component
 *
 * Reusable button with loading spinner animation.
 *
 * @component
 * @example
 * ```tsx
 * <LoadingButton
 *   onClick={handleClick}
 *   disabled={false}
 *   loading={isLoading}
 *   loadingText="Processing..."
 *   ariaLabel="Submit form"
 * >
 *   Submit
 * </LoadingButton>
 * ```
 */
export const LoadingButton: React.FC<LoadingButtonProps> = ({
  onClick,
  disabled,
  loading,
  loadingText,
  children,
  ariaLabel,
}) => {
  const isInteractive = !loading && !disabled;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex justify-center py-3 px-8 border border-transparent shadow-sm text-base rounded-md text-white bg-dataops-primary-blue hover:bg-hover-primary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
      whileHover={isInteractive ? { scale: 1.02 } : {}}
      whileTap={isInteractive ? { scale: 0.98 } : {}}
      aria-label={ariaLabel ?? (loading ? loadingText : undefined)}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <motion.span
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full"
            aria-hidden="true"
          />
          {loadingText}
        </span>
      ) : (
        children
      )}
    </motion.button>
  );
};
